import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import { CreateVehicleData, UpdateVehicleData } from 'api/vehicle';
import {
  PaginationData,
  Vehicle,
} from 'constants/interface';
import { RootState } from 'state';

const vehicleEntityAdapter = createEntityAdapter<Vehicle>();

export interface VehiclesState extends EntityState<Vehicle> {
  isLoading: boolean
  remoteTotal: number
}

export const initialState: VehiclesState = vehicleEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
});

export const createVehicleAction = createAsyncThunk(
  'vehicles/create',
  async (payload: CreateVehicleData) => {
    const vehicleData = await apiClient.vehicle.createVehicle(payload);
    return vehicleData;
  },
);

export const updateVehicleAction = createAsyncThunk(
  'vehicles/update',
  async (payload: UpdateVehicleData) => {
    const vehicleData = await apiClient.vehicle.updateVehicle(payload);
    return vehicleData;
  },
);

export const getVehicleAction = createAsyncThunk(
  'vehicles/get',
  async (payload: {
    vehicleId: string,
  }) => {
    const { vehicleId } = payload;
    const response = await apiClient.vehicle.getVehicle(vehicleId);

    return response;
  },
);

export const getVehicleListAction = createAsyncThunk(
  'vehicles/list',
  async (payload: PaginationData) => {
    const response = await apiClient
      .vehicle
      .getVehicleList(payload);

    return response;
  },
);

export const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  reducers: {
    cleanVehiclesAction: vehicleEntityAdapter.removeAll,
  },
  extraReducers: (builder) => {
    // List
    builder.addCase(getVehicleListAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getVehicleListAction.fulfilled, (state, action) => {
      vehicleEntityAdapter.removeAll(state);
      vehicleEntityAdapter.addMany(state, action.payload.data);
      state.remoteTotal = action.payload.pagination.total;
      state.isLoading = false;
    });
    builder.addCase(getVehicleListAction.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  cleanVehiclesAction,
} = vehiclesSlice.actions;

// Selectors

export const isLoadingVehiclesSelector = (state: RootState):
  boolean => state.vehicles.isLoading;

export const vehiclesEntitiesSelector = (state: RootState):
  Dictionary<Vehicle> => state.vehicles.entities;

export const vehiclesIdsSelector = (state: RootState):
  EntityId[] => state.vehicles.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.vehicles.remoteTotal;
